/**
 * EmptyState — the one "there is nothing here" (`STYLE.md` §9).
 *
 * The 2026-09-16 review counted **thirteen** of these in one product: centred ones, dashed
 * ones, bare sentences in a `<p>`, a muted paragraph inside a card, one that was an `<h3>`
 * and entered the document outline. An empty list is the most common state a new workspace
 * has, so it is the state a new customer sees most, and it looked like thirteen products.
 *
 * TWO VARIANTS, AND THE DIFFERENCE IS WHERE IT IS DRAWN
 *
 * | Variant | Where | How |
 * |---|---|---|
 * | `list` | in place of the rows | a dashed box (`1px dashed --neutral-300`, `--r-md`, `--space-8`) — the outline of the thing that is missing |
 * | `screen` | at the top of an otherwise empty body | a plain block, no border — there is no container for it to be inside |
 *
 * **Left-aligned, never centred.** Centring is what an empty state does when it is trying to
 * be an illustration. This one is trying to be the first line of the screen, so it starts
 * where every other line on the screen starts, and its body is capped at 52ch because a
 * sentence that runs the width of a list page is not read.
 *
 * **The title is a `<p>`, not a heading.** It sits where rows would be — inside a section that
 * already has a `SectionTitle` or under a `ScreenHead` — so promoting it to `h2`/`h3` would
 * insert a heading into the outline that disappears the moment the first row lands, which is
 * a document whose structure depends on how much data a workspace happens to hold.
 *
 * **No `role="status"`.** A live region announces *changes*, and this element is inserted
 * already-final, replacing the skeleton whose `role="status"` did the announcing while the
 * fetch was in flight. A region that never updates after it mounts is announced by some
 * readers and not others; the text is ordinary content in the reading order instead, where a
 * reader arriving at the list finds it the same way a sighted person does.
 *
 * **`explains` is required, and that is the point of it** (`KLQ-20261024-001` §B, `-014`). The
 * UX night counted seventeen empty states and found most of them said what was absent —
 * „Nič nevisí“ — and not what the absent thing *is*. An empty list is the first time a new
 * studio meets a noun, so the sentence that names it is not optional copy: a title-only
 * `<EmptyState>` does not compile, and `scripts/guard.mjs` rule 13 fails the build on a JSX
 * element without `explains=` as well. `explains` is one sentence, what the thing is (≤ 15
 * words SK); `body` stays for *why* it is empty here and what to do about it.
 *
 * The single `action` is drawn **inside** the box, under the text — the same action the head
 * carries, repeated where the eye already is (design brief §7). On six screens the head's
 * action sat in the opposite corner from the empty box.
 *
 * Every string is a prop — `title`, `explains` and `body` come from the feature's catalog, and
 * the single `action` is a `Button` the caller already built. This component owns no copy.
 */

import type { ReactNode } from "react";

import styles from "./EmptyState.module.css";

export type EmptyStateVariant = "list" | "screen";

export interface EmptyStateProps {
  /** 16/24 sans 600. A statement of what is absent, not an apology. */
  readonly title: string;
  /** 13/18 `--neutral-700`, one sentence: what the thing is. Required — see the module note. */
  readonly explains: string;
  /** 13/18 `--neutral-500`, ≤ 52ch. Why it is empty here, and what to do about it. */
  readonly body?: string | undefined;
  /** At most one. A second action here is a screen that has not decided what it wants. */
  readonly action?: ReactNode | undefined;
  /** `list` where the rows would be, `screen` when the whole body is empty. No default. */
  readonly variant: EmptyStateVariant;
}

export function EmptyState({ title, explains, body, action, variant }: EmptyStateProps) {
  const classes = [styles.empty, variant === "list" ? styles.list : styles.screen].join(" ");

  return (
    <div className={classes}>
      <p className={styles.title}>{title}</p>
      <p className={styles.explains}>{explains}</p>
      {body === undefined ? null : <p className={styles.body}>{body}</p>}
      {action === undefined ? null : <div className={styles.action}>{action}</div>}
    </div>
  );
}
